/** Compliance types shown in hero overview and filters (initial release — no zoning). */
export const OVERVIEW_TYPES = ['building_code', 'client_requirements', 'standards'];

const COMPLIANCE_TYPE_LABELS = {
  building_code: 'Building Code',
  client_requirements: 'Client Req.',
  zoning: 'Zoning',
  standards: 'Standards',
};

const PATH_TYPE_LABELS = {
  conflict: 'Conflict',
  gap: 'Gap',
  compliance_check: 'Compliance Check',
};

const VERDICT_LABELS = {
  compliant: 'Compliant',
  non_compliant: 'Non-Compliant',
  needs_review: 'Needs Review',
  not_applicable: 'Not Applicable',
  uncertain: 'Uncertain',
  information_not_available: 'Info Not Available',
};

const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In Progress',
  'in-progress': 'In Progress',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
  superseded: 'Superseded',
  stale: 'Stale',
  review: 'Review',
};

export function normalizeIssueStatus(status) {
  if (!status) return 'open';
  const s = String(status).trim().toLowerCase().replace(/-/g, '_');
  if (s === 'inprogress') return 'in_progress';
  if (['open', 'in_progress', 'resolved', 'dismissed', 'review', 'superseded', 'stale'].includes(s)) {
    return s;
  }
  return 'open';
}

export function normalizeStatusKey(status) {
  return normalizeIssueStatus(status);
}

export function statusBadgeClass(status) {
  const key = normalizeStatusKey(status);
  return `badge badge-${key}`;
}

const AVATAR_COLORS = [
  '#e97024',
  '#2563eb',
  '#388e3c',
  '#7c3aed',
  '#db2777',
  '#0891b2',
  '#ca8a04',
];

export function memberInitials(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return String(name).slice(0, 2).toUpperCase();
}

export function memberAvatarColor(userId) {
  const id = String(userId || '');
  if (!id) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash + id.charCodeAt(i) * (i + 1)) % 2147483647;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

export function findProjectMember(projectMembers, userId) {
  if (!userId) return null;
  return (projectMembers || []).find((m) => m.userId === userId) || null;
}

export function verdictLabel(verdict) {
  return VERDICT_LABELS[verdict] || verdict || '—';
}

export function complianceTypeLabel(type) {
  return COMPLIANCE_TYPE_LABELS[type] || type || '—';
}

export function pathTypeLabel(pathType) {
  return PATH_TYPE_LABELS[pathType] || pathType || '—';
}

export function statusLabel(status) {
  return STATUS_LABELS[normalizeStatusKey(status)] || status || '—';
}

export function formatCreatedAt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).replace(',', ' ·');
}

/** Date + time for dashboard “Last Scan” subtitle. */
export function formatLastScanAt(iso) {
  return formatCreatedAt(iso);
}

export function normalizeComplianceType(raw) {
  if (!raw) return '';
  const s = String(raw).toLowerCase().replace(/\s+/g, '_');
  if (s.includes('building')) return 'building_code';
  if (s.includes('client')) return 'client_requirements';
  if (s.includes('zoning')) return 'zoning';
  if (s.includes('standard')) return 'standards';
  if (OVERVIEW_TYPES.includes(s)) return s;
  return '';
}

/** Resolve normalized compliance type from all common API shapes. */
export function getIssueComplianceType(issue) {
  if (!issue) return '';
  const candidates = [
    issue.complianceType,
    issue.compliance_type,
    ...(Array.isArray(issue.categories) ? issue.categories : []),
    issue.category,
  ].filter(Boolean);

  for (const raw of candidates) {
    const normalized = normalizeComplianceType(raw);
    if (OVERVIEW_TYPES.includes(normalized)) return normalized;
  }
  return '';
}

/** Resolve verdict from issue root, finding_detail, or tags. */
export function getIssueVerdict(issue) {
  if (!issue) return null;
  const direct = issue.verdict || issue.finding_detail?.verdict;
  if (direct && direct !== 'not_applicable') return direct;

  const tagVerdicts = ['compliant', 'non_compliant', 'needs_review', 'uncertain', 'information_not_available'];
  const fromTags = (issue.tags || []).find((t) => tagVerdicts.includes(String(t)));
  if (fromTags) return fromTags;

  return null;
}

/** Parse page number from API location field (string or number). */
export function parsePageNumber(value) {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : parseInt(String(value).trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Display title for deliverable doc_name (flattened id or human-readable). */
export function friendlyDeliverableTitle(docName) {
  if (!docName || typeof docName !== 'string') return '';
  const raw = docName.trim();
  if (!raw.includes('-links-')) return raw;
  let s = raw.startsWith('file-') ? raw.slice(5) : raw;
  const procIdx = s.indexOf('-processed-');
  if (procIdx >= 0) {
    const tail = s.slice(procIdx + '-processed-'.length).replace(/-txt$/i, '');
    return tail.replace(/-/g, ' ') || raw;
  }
  return raw;
}

/**
 * Inverse of backend normalizeFileIdFromS3Key fileSlug (lossy for some titles).
 * e.g. Ontario-Building-Code-2024-Doors → Ontario_Building_Code_2024_-_Doors
 */
export function originalStemFromFileSlug(fileSlug) {
  const slug = String(fileSlug || '').replace(/-txt$/i, '');
  if (!slug) return '';
  const plain = slug.replace(/-/g, '_');
  const withTitleDash = plain.replace(/(\d{4})_([A-Z])/g, '$1_-_$2');
  return withTitleDash !== plain ? withTitleDash : plain;
}

/**
 * Reconstruct S3 original PDF key from flattened OpenSearch documentId / doc_name.
 * e.g. file-projects-{pid}-requirements-{reqId}-links-{linkId}-processed-{base}-txt
 */
export function inferOriginalPdfKeyFromDocumentId(documentId, projectId, requirementIdHint) {
  if (!documentId || typeof documentId !== 'string') return null;
  if (documentId.includes('/')) {
    if (documentId.includes('/original/')) return documentId;
    if (documentId.includes('/processed/')) {
      return documentId.replace('/processed/', '/original/').replace(/\.txt$/i, '.pdf');
    }
  }
  if (!documentId.includes('-links-')) return null;

  let s = documentId.startsWith('file-') ? documentId.slice(5) : documentId;
  const linksIdx = s.indexOf('-links-');
  if (linksIdx < 0) return null;

  const beforeLinks = s.slice(0, linksIdx);
  const afterLinks = s.slice(linksIdx + '-links-'.length);
  const procSplit = afterLinks.split('-processed-');
  if (procSplit.length < 2) return null;

  const linkId = procSplit[0];
  const stem = originalStemFromFileSlug(procSplit[1]);
  if (!linkId || !stem) return null;

  let pid = projectId || null;
  if (beforeLinks.startsWith('projects-')) {
    const afterProjects = beforeLinks.slice('projects-'.length);
    const reqIdx = afterProjects.indexOf('-requirements-');
    pid = reqIdx >= 0 ? afterProjects.slice(0, reqIdx) : afterProjects;
  }

  let requirementId = null;
  const reqMatch = s.match(/-requirements-(.+?)-links-/);
  if (reqMatch) {
    requirementId = reqMatch[1];
  } else if (
    requirementIdHint &&
    s.includes(`-requirements-${requirementIdHint}-links-`)
  ) {
    requirementId = requirementIdHint;
  }

  if (!pid) return null;

  if (requirementId) {
    return `projects/${pid}/requirements/${requirementId}/links/${linkId}/original/${stem}.pdf`;
  }

  return `projects/${pid}/links/${linkId}/original/${stem}.pdf`;
}

export function coerceSectionRef(sectionRef) {
  if (sectionRef == null || sectionRef === '') return '';
  if (typeof sectionRef === 'string') return sectionRef.trim();
  if (typeof sectionRef === 'object') {
    return String(sectionRef.value || sectionRef.article || sectionRef.label || '').trim();
  }
  return '';
}

function parseFindingDetail(raw) {
  if (!raw) return null;
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === 'object' && parsed ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

function refHasDisplayText(ref) {
  return !!(ref && String(ref.label || ref.value || '').trim());
}

/** True when value looks like a code section ref (e.g. 3.3.1.4.), not full requirement prose. */
export function looksLikeArticleCitation(value) {
  const s = String(value || '').trim();
  if (!s) return false;
  if (s.length > 64) return false;
  if (/^\(\d+\)\s/.test(s)) return false;
  if (/^[A-Za-z]?\d+(?:\.\d+){1,}\.?$/u.test(s)) return true;
  return /^[A-Za-z]?\d+(?:\.\d+)*\.?$/u.test(s) && s.length <= 24;
}

/** Requirement column label: prefer ExtractedRequirements.article when requirement_id matches. */
export function resolveRequirementArticle(issue, reg) {
  const regArticle = coerceSectionRef(reg?.article);
  const issueSection =
    coerceSectionRef(issue.section_ref || issue.sectionRef) ||
    coerceSectionRef(issue.requirementArticle);

  if (reg && regArticle) return regArticle;
  if (looksLikeArticleCitation(issueSection)) return issueSection;
  return regArticle || '';
}

/** Requirement register article ref (matches Extracted Requirements first column). */
export function parseRequirementArticleRef(article, extras = {}) {
  if (!article) return null;
  const label = String(article).trim();
  return {
    type: '',
    value: label,
    label,
    originalPdfKey: extras.originalPdfKey || null,
    pageNumber: extras.pageNumber ?? null,
    fileId: extras.fileId || extras.sourceDocId || null,
  };
}

export function parseStructuredRef(ref) {
  if (!ref) return null;
  if (typeof ref === 'string') {
    return parseRequirementArticleRef(ref);
  }
  const type = ref.type || ref.refType || 'Section';
  const value = ref.value || ref.refKey || ref.label || '';
  return {
    type,
    value,
    label: ref.label || value,
    originalPdfKey: ref.originalPdfKey || ref.original_pdf_key || ref.key || null,
    pageNumber: ref.pageNumber ?? ref.page ?? null,
    pages: ref.pages,
    fileId: ref.fileId,
  };
}

export function parseSourceBlock(src) {
  if (!src || typeof src !== 'object') return null;
  const refsRaw = src.references || src.refs || [];
  const references = (Array.isArray(refsRaw) ? refsRaw : []).map(parseStructuredRef).filter(Boolean);
  const docName = friendlyDeliverableTitle(
    src.docName || src.doc_name || src.title || src.name || ''
  );
  const pageNumber =
    parsePageNumber(src.pageNumber ?? src.page_number) ??
    parsePageNumber(src.location ?? src.loc);
  const originalPdfKey =
    src.originalPdfKey ||
    src.original_pdf_key ||
    inferOriginalPdfKeyFromDocumentId(
      src.documentId || src.document_id,
      src.projectId,
      null
    ) ||
    inferOriginalPdfKeyFromDocumentId(
      src.docName || src.doc_name,
      src.projectId,
      null
    ) ||
    references.find((r) => r.originalPdfKey)?.originalPdfKey ||
    null;

  if (originalPdfKey && pageNumber && !references.some((r) => r.type === 'Page')) {
    references.unshift({
      type: 'Page',
      value: String(pageNumber),
      label: `p. ${pageNumber}`,
      originalPdfKey,
      pageNumber,
    });
  }

  return {
    docName,
    location: src.location || src.loc || '',
    statement: src.statement || src.text || '',
    highlightExcerpt: src.excerpt || src.statement || src.text || '', 
    references,
    pageNumber,
    id: src.id || src.fileId || src.doc_id || null,
    originalPdfKey,
    projectId: src.projectId || src.project_id || null,
  };
}

function parseSourceDocuments(raw) {
  if (!raw) return [];
  let arr = raw;
  if (typeof raw === 'string') {
    try {
      arr = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(arr)) return [];
  return arr.map((item) => {
    if (typeof item === 'string') {
      try {
        return JSON.parse(item);
      } catch {
        return { title: item };
      }
    }
    return item;
  });
}

/** Merge extracted-requirements register row for PDF open + article display. */
export function applyRequirementRegisterContext(issue, registerById) {
  if (!issue || !registerById?.size) return issue;

  const rid = issue.requirement_id || issue.requirementId;
  const reg = rid ? registerById.get(String(rid)) : null;
  const article = resolveRequirementArticle(issue, reg);

  const sourceDocId = reg?.sourceDocId || reg?.source_doc_id || null;
  const requirementPdfKey =
    issue.requirementPdfKey ||
    reg?.originalPdfKey ||
    reg?.original_pdf_key ||
    inferOriginalPdfKeyFromDocumentId(sourceDocId, issue.project_id) ||
    null;

  const requirementRefs = [...(issue.requirementRefs || [])];
  if (article) {
    const articleRef = parseRequirementArticleRef(article, {
      originalPdfKey: requirementPdfKey,
      pageNumber: reg?.pageNumber ?? reg?.page_number,
      fileId: sourceDocId || rid,
      sourceDocId,
    });
    const dup = requirementRefs.some((r) => r.value === articleRef.value);
    if (!dup) requirementRefs.unshift(articleRef);
    else {
      const idx = requirementRefs.findIndex((r) => r.value === articleRef.value);
      if (idx >= 0) {
        requirementRefs[idx] = { ...requirementRefs[idx], ...articleRef };
      }
    }
  }

  return {
    ...issue,
    requirementArticle: article,
    requirementPdfKey,
    requirementRegister: reg || issue.requirementRegister || null,
    requirementRefs,
  };
}

export function enrichIssueFields(base) {
  const fd = parseFindingDetail(base.finding_detail || base.findingDetail) || {};
  const sourceDocuments = parseSourceDocuments(base.sourceDocuments);
  const firstDoc = sourceDocuments[0] || null;
  const projectId = base.project_id || base.projectId || null;
  const deliverableDocIds = base.deliverable_doc_ids || base.deliverableDocIds || [];
  const requirementId = base.requirement_id || base.requirementId || null;

  const requirementRefs = [];
  const sectionRef = coerceSectionRef(base.section_ref || base.sectionRef || fd.section_ref);
  if (sectionRef && looksLikeArticleCitation(sectionRef)) {
    const articleRef = parseRequirementArticleRef(sectionRef);
    if (articleRef) requirementRefs.push(articleRef);
  }
  (base.references || []).forEach((r) => {
    const parsed = parseStructuredRef(r);
    if (
      parsed &&
      refHasDisplayText(parsed) &&
      !requirementRefs.some((x) => x.value === parsed.value)
    ) {
      requirementRefs.push(parsed);
    }
  });
  if (fd.section_ref) {
    const parsed = parseStructuredRef(coerceSectionRef(fd.section_ref) || fd.section_ref);
    if (
      parsed &&
      refHasDisplayText(parsed) &&
      !requirementRefs.some((x) => x.value === parsed.value)
    ) {
      requirementRefs.push(parsed);
    }
  }
  if (fd.code_refs) {
    (Array.isArray(fd.code_refs) ? fd.code_refs : [fd.code_refs]).forEach((r) => {
      const parsed = parseStructuredRef(r);
      if (parsed && refHasDisplayText(parsed)) requirementRefs.push(parsed);
    });
  }
  const presentIn = parseSourceBlock(fd.presentIn || fd.present_in);
  const absentFrom = (fd.absentFrom || fd.absent_from || []).map((d) =>
    typeof d === 'string' ? { docName: d } : parseSourceBlock(d) || { docName: d.docName || '' }
  );
  const sourceA = parseSourceBlock(fd.sourceA || fd.source_a);
  const sourceB = parseSourceBlock(fd.sourceB || fd.source_b);
  const conflictMode = fd.conflict_mode || fd.conflictMode || null;

  const deliverableEvidence = fd.deliverable_evidence || fd.deliverableEvidence;
  const evidenceFromDeliverable = deliverableEvidence
    ? parseSourceBlock({
        docName: deliverableEvidence.doc_name || deliverableEvidence.docName,
        location: deliverableEvidence.location,
        statement: deliverableEvidence.statement,
        pageNumber: deliverableEvidence.page_number ?? deliverableEvidence.pageNumber,
        id: deliverableDocIds[0] || deliverableEvidence.doc_id || deliverableEvidence.docId,
        documentId: deliverableEvidence.doc_name || deliverableEvidence.docName,
        projectId,
        originalPdfKey:
          deliverableEvidence.originalPdfKey ||
          deliverableEvidence.original_pdf_key,
      })
    : null;

  const evidenceFromDetail =
    evidenceFromDeliverable ||
    parseSourceBlock({ ...fd.evidence, projectId }) ||
    parseSourceBlock({ ...fd.source, projectId }) ||
    (fd.doc_name
      ? parseSourceBlock({
          docName: fd.doc_name,
          location: fd.location,
          statement: fd.statement,
          references: fd.references,
          pageNumber: fd.page_number,
          projectId,
        })
      : null);

  const evidenceFromDoc = firstDoc
    ? parseSourceBlock({
        docName: firstDoc.title || firstDoc.name || firstDoc.fileName,
        location: firstDoc.location,
        statement: firstDoc.statement || firstDoc.snippet,
        references: firstDoc.references,
        pageNumber: firstDoc.pageNumber || firstDoc.page,
        id: firstDoc.id || firstDoc.fileId,
      })
    : null;

  const conflictEvidence = sourceA || sourceB || null;
  const evidenceSource =
    (evidenceFromDetail && evidenceFromDetail.docName ? evidenceFromDetail : null) ||
    (evidenceFromDoc && evidenceFromDoc.docName ? evidenceFromDoc : null) ||
    conflictEvidence ||
    evidenceFromDetail ||
    evidenceFromDoc ||
    null;
  const evidenceDocMeta = evidenceSource
    ? {
        id: evidenceSource.id || deliverableDocIds[0] || firstDoc?.id || firstDoc?.fileId,
        title: evidenceSource.docName,
        pageNumber: evidenceSource.pageNumber,
        originalPdfKey: evidenceSource.originalPdfKey || null,
        references: evidenceSource.references || [],
        projectId,
      }
    : firstDoc
      ? {
          id: firstDoc.id || firstDoc.fileId,
          title: firstDoc.title || firstDoc.name,
          pageNumber: firstDoc.pageNumber || firstDoc.page,
          originalPdfKey: firstDoc.originalPdfKey || firstDoc.original_pdf_key || null,
          references: (firstDoc.references || []).map(parseStructuredRef).filter(Boolean),
          projectId,
        }
      : null;

  const researchFinding =
    base.identifiedIssue ||
    base.finding_description ||
    base.description ||
    '';

  const complianceType = getIssueComplianceType({
    ...base,
    complianceType: base.complianceType,
    compliance_type: base.compliance_type,
    categories: base.categories,
  });

  const verdict = getIssueVerdict({
    ...base,
    verdict: base.verdict || base.finding_detail?.verdict,
    finding_detail: base.finding_detail,
    tags: base.tags,
  });

  const overrideType =
    base.overrideType ||
    base.override_type ||
    fd.override_type ||
    fd.overrideType ||
    null;
  const dismissalReason =
    base.dismissalReason ||
    base.dismissal_reason ||
    fd.dismissal_reason ||
    fd.dismissalReason ||
    null;
  const resolutionAttachments = (() => {
    const raw =
      base.resolutionAttachments ||
      base.resolution_attachments ||
      fd.resolution_attachments ||
      fd.resolutionAttachments;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  })();

  return {
    ...base,
    sourceDocuments,
    complianceType,
    verdict: verdict || base.verdict || base.finding_detail?.verdict || null,
    pathType: base.path_type || base.pathType || '',
    requirementArticle: looksLikeArticleCitation(sectionRef) ? sectionRef : '',
    requirement_id: base.requirement_id || base.requirementId || '',
    deliverable_doc_ids: deliverableDocIds,
    project_id: projectId,
    requirementRefs,
    researchFinding,
    evidenceDoc: evidenceSource?.docName || evidenceDocMeta?.title || '',
    evidenceLocation: evidenceSource?.location || '',
    evidenceStatement: evidenceSource?.statement || '',
    evidenceDocMeta,
    verdictLabel: verdictLabel(verdict || base.verdict),
    presentIn,
    absentFrom,
    sourceA,
    sourceB,
    conflictMode,
    overrideType,
    dismissalReason,
    resolutionAttachments,
    formattedCreatedAt: formatCreatedAt(base.createdAt),
  };
}

/** Parse linked-folder path from an original PDF S3 key. */
export function parseRequirementLinkFromPdfKey(originalPdfKey) {
  const key = String(originalPdfKey || '');
  const m = key.match(/\/requirements\/([^/]+)\/links\/([^/]+)\/original\/([^/]+)$/);
  if (!m) return null;
  const filename = m[3];
  const stem = filename.replace(/\.[^.]+$/i, '');
  return {
    requirementId: m[1],
    linkId: m[2],
    filename,
    sheetId: stem,
  };
}

function deliverableSourceFromBlock(block, projectId) {
  if (!block || typeof block !== 'object') return null;
  const originalPdfKey =
    block.originalPdfKey ||
    block.original_pdf_key ||
    block.sourcePdfKey ||
    inferOriginalPdfKeyFromDocumentId(block.documentId || block.document_id, projectId) ||
    inferOriginalPdfKeyFromDocumentId(block.docName || block.doc_name, projectId);
  if (!originalPdfKey) return null;
  const link = parseRequirementLinkFromPdfKey(originalPdfKey);
  if (!link) return null;
  return {
    ...link,
    originalPdfKey,
    pageNumber: parsePageNumber(block.pageNumber ?? block.page_number ?? block.location ?? block.loc),
    docName: block.docName || block.doc_name || block.title || link.filename,
    statement: block.statement || block.text || '',
    location: block.location || block.loc || '',
    componentId: block.componentId || block.component_id || null,
  };
}

/** Deliverable PDF sources on a compliance issue that can open in Review. */
export function collectIssueDeliverableSources(issue) {
  if (!issue) return [];
  const projectId = issue.project_id || issue.projectId || null;
  const blocks = [];
  const geo = issue.source_geometry_ref || issue.sourceGeometryRef;
  if (geo) blocks.push(geo);
  if (issue.evidenceDocMeta?.originalPdfKey || issue.evidenceDoc) {
    blocks.push({
      docName: issue.evidenceDoc,
      location: issue.evidenceLocation,
      statement: issue.evidenceStatement,
      pageNumber: issue.evidenceDocMeta?.pageNumber,
      originalPdfKey: issue.evidenceDocMeta?.originalPdfKey,
      projectId,
    });
  }
  if (issue.presentIn) blocks.push(issue.presentIn);
  if (issue.sourceA) blocks.push(issue.sourceA);
  if (issue.sourceB) blocks.push(issue.sourceB);

  const seen = new Set();
  const out = [];
  for (const block of blocks) {
    const parsed = deliverableSourceFromBlock(block, projectId);
    if (!parsed || seen.has(parsed.originalPdfKey)) continue;
    seen.add(parsed.originalPdfKey);
    out.push(parsed);
  }
  return out;
}

export function getIssueDrawingReviewTarget(issue) {
  return collectIssueDeliverableSources(issue)[0] || null;
}

export function issueHasDrawingReviewTarget(issue) {
  return collectIssueDeliverableSources(issue).length > 0;
}

/** Navigation payload for Review tab (Compliance → View on drawing). */
export function buildReviewNavStateFromIssue(issue, extras = {}) {
  const target = getIssueDrawingReviewTarget(issue);
  if (!target) return null;
  return {
    mode: 'issue',
    issueId: issue.id || issue.issueId,
    requirementId: target.requirementId,
    linkId: target.linkId,
    folderTitle: extras.folderTitle || target.docName || issue.topic || 'Drawing',
    originalPdfKey: target.originalPdfKey,
    pageNumber: target.pageNumber,
    sheetId: target.sheetId,
    componentId: target.componentId,
    highlightText: target.statement || issue.evidenceStatement || issue.identifiedIssue || '',
  };
}

export const OVERRIDE_TYPE_OPTIONS = [
  { value: 'not_applicable_to_project', label: 'Not applicable to this project' },
  { value: 'requirement_satisfied', label: 'Requirement satisfied — scan missed evidence' },
  { value: 'duplicate_finding', label: 'Duplicate finding' },
  { value: 'other', label: 'Other' },
];

export function formatResolutionFileSize(bytes) {
  if (bytes == null || Number.isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Validate Zone 3 fields before dismissed. Override reason is the only required
 * field, and only when dismissing — resolved has no required fields, and notes /
 * resolution evidence are always optional regardless of override type.
 */
export function validateResolutionForStatus(status, { overrideType } = {}) {
  const key = normalizeStatusKey(status);
  if (key !== 'dismissed') {
    return { valid: true, errors: {} };
  }

  const errors = {};
  if (!overrideType) {
    errors.overrideType = true;
  }

  return { valid: Object.keys(errors).length === 0, errors };
}

export function computeComplianceOverview(issues) {
  const list = issues || [];
  const totalIssues = list.length;
  const byType = {};
  OVERVIEW_TYPES.forEach((t) => {
    byType[t] = { compliant: 0, withVerdict: 0, issueCount: 0, pct: null };
  });

  list.forEach((issue) => {
    const type = getIssueComplianceType(issue);
    if (!type || !byType[type]) return;

    byType[type].issueCount += 1;
    const v = getIssueVerdict(issue);
    if (!v) return;

    byType[type].withVerdict += 1;
    if (v === 'compliant') byType[type].compliant += 1;
  });

  const typePcts = [];
  OVERVIEW_TYPES.forEach((t) => {
    const bucket = byType[t];
    if (bucket.issueCount === 0) {
      bucket.pct = null;
    } else if (bucket.withVerdict > 0) {
      bucket.pct = Math.round((100 * bucket.compliant) / bucket.withVerdict);
      typePcts.push(bucket.pct);
    } else {
      bucket.pct = null;
    }
  });

  const overall =
    typePcts.length > 0
      ? Math.round(typePcts.reduce((a, b) => a + b, 0) / typePcts.length)
      : null;

  return { byType, overall, totalIssues };
}

/** Primary requirement ref for table pill (article text + PDF key/page). */
export function getPrimaryRequirementRef(issue) {
  if (!issue) return null;

  const articleText = coerceSectionRef(
    issue.requirementArticle ||
    issue.requirementRegister?.article ||
    (looksLikeArticleCitation(issue.section_ref || issue.sectionRef)
      ? coerceSectionRef(issue.section_ref || issue.sectionRef)
      : '')
  );

  const enriched = (refs) =>
    refs
      .map((r) => ({
        ...r,
        label: articleText || r.label || r.value,
        value: articleText || r.value || r.label,
        originalPdfKey: r.originalPdfKey || issue.requirementPdfKey || null,
        pageNumber: r.pageNumber ?? issue.requirementRegister?.pageNumber ?? null,
        fileId: r.fileId || issue.requirementRegister?.sourceDocId || issue.requirement_id,
      }))
      .filter(refHasDisplayText);

  const refs = enriched(issue.requirementRefs || []);
  const withKey = refs.find((r) => r.originalPdfKey);
  if (withKey) return withKey;
  if (refs[0]) return refs[0];

  if (articleText) {
    return parseRequirementArticleRef(articleText, {
      originalPdfKey: issue.requirementPdfKey,
      pageNumber: issue.requirementRegister?.pageNumber,
      fileId: issue.requirementRegister?.sourceDocId || issue.requirement_id,
      sourceDocId: issue.requirementRegister?.sourceDocId,
    });
  }

  return null;
}

export function computeIssueKpis(issues) {
  const counts = { open: 0, in_progress: 0, resolved: 0, dismissed: 0 };
  (issues || []).forEach((issue) => {
    const key = normalizeStatusKey(issue.status);
    if (Object.prototype.hasOwnProperty.call(counts, key)) counts[key] += 1;
  });
  return counts;
}

export function issueMatchesComplianceFilters(issue, filters) {
  const {
    query,
    filterStatus,
    filterComplianceType,
    filterPathType,
    filterAssigned,
    filterScanId,
    activeStatFilter,
    currentUserId,
  } = filters;

  const statusKey = normalizeStatusKey(issue.status);

  if (activeStatFilter && activeStatFilter !== 'all') {
    if (activeStatFilter === 'my-assigned') {
      if (!currentUserId || issue.assignedTo !== currentUserId) return false;
    } else if (activeStatFilter === 'non-compliant') {
      if (issue.verdict !== 'non_compliant') return false;
    } else if (activeStatFilter === 'needs-review') {
      if (issue.verdict !== 'needs_review') return false;
    } else if (statusKey !== activeStatFilter) {
      return false;
    }
  }

  if (filterStatus && statusKey !== normalizeStatusKey(filterStatus)) return false;
  if (filterComplianceType && getIssueComplianceType(issue) !== filterComplianceType) return false;
  if (filterPathType && issue.pathType !== filterPathType) return false;
  if (filterScanId && issue.scan_id !== filterScanId) return false;
  if (filterAssigned === 'unassigned') {
    if (issue.assignedTo) return false;
  } else if (filterAssigned && issue.assignedTo !== filterAssigned) {
    return false;
  }

  const q = (query || '').trim().toLowerCase();
  if (q) {
    const blob = [
      issue.ncCode,
      issue.topic,
      issue.identifiedIssue,
      issue.evidenceDoc,
      issue.expectedCompliance,
      issue.complianceType,
      issue.pathType,
      ...(issue.requirementRefs || []).map((r) => r.label || r.value),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    if (!blob.includes(q)) return false;
  }

  return true;
}
